/**
 * Hook for managing the public interview chat experience.
 *
 * Handles:
 * - Token resolution on mount
 * - Starting / resuming the interview
 * - SSE streaming with suggested answer chips
 * - Review & verify step
 * - Interview completion and summary
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import publicInterviewService from '@/lib/api/publicInterview';
import type {
  PublicInterviewerInfo,
  PublicStreamEvent,
  TopicInfo,
  TopicReviewSummary,
} from '@/lib/api/publicInterview';

// ========================================
// TYPES
// ========================================

export type InterviewPhase =
  | 'loading'
  | 'email'
  | 'starting'
  | 'chatting'
  | 'generating_review'
  | 'reviewing'
  | 'confirming'
  | 'complete';

export interface ChatMessage {
  id: string;
  speaker: 'interviewer' | 'user';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  suggestedAnswers?: string[];
  suggestionSelectionType?: 'single' | 'multi';
}

const createMessageId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// ========================================
// HOOK
// ========================================

export function usePublicInterview(token: string) {
  // Phase-based state (replaces isLoading, isStarting, isComplete, isCompleting)
  const [phase, setPhase] = useState<InterviewPhase>('loading');

  // Core state
  const [interviewerInfo, setInterviewerInfo] = useState<PublicInterviewerInfo | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalTopics, setTotalTopics] = useState(0);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [topics, setTopics] = useState<TopicInfo[]>([]);
  const [topicConfidence, setTopicConfidence] = useState<Record<string, number>>({});

  // Orthogonal booleans (overlay on top of phase, not exclusive)
  const [isStreaming, setIsStreaming] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isUpdatingReview, setIsUpdatingReview] = useState(false);

  // Review state
  const [reviewSummaries, setReviewSummaries] = useState<TopicReviewSummary[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const reviewTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // ========================================
  // MESSAGE HELPERS
  // ========================================

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id' | 'timestamp'>): string => {
    const id = createMessageId();
    setMessages((prev) => [...prev, { ...msg, id, timestamp: new Date() }]);
    return id;
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<ChatMessage>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...updates } : m)));
  }, []);

  const clearSuggestedAnswers = useCallback(() => {
    setMessages((prev) =>
      prev.map((m) => (m.suggestedAnswers ? { ...m, suggestedAnswers: undefined } : m))
    );
  }, []);

  // ========================================
  // TOKEN RESOLUTION
  // ========================================

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    (async () => {
      setPhase('loading');
      setError(null);
      try {
        const info = await publicInterviewService.resolveToken(token);
        if (!cancelled) {
          setInterviewerInfo(info);
          setTotalTopics(info.total_topics);
          if (info.topics?.length) setTopics(info.topics);
          setPhase('email');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Interview not found');
          setPhase('email');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Cleanup: cancel any pending review transition timeout on unmount
  useEffect(() => {
    return () => {
      if (reviewTransitionTimeoutRef.current) clearTimeout(reviewTransitionTimeoutRef.current);
    };
  }, []);

  // ========================================
  // ENTER REVIEW
  // ========================================

  const enterReview = useCallback(async () => {
    if (!token || !sessionId) return;
    if (phaseRef.current !== 'chatting') return; // guard: prevent double-trigger (use ref to avoid stale closure)

    setPhase('generating_review');
    setError(null);

    try {
      const result = await publicInterviewService.generateReview(token, sessionId);
      setReviewSummaries(result.topic_summaries);
      setTopicConfidence(result.topic_confidence);
      setPhase('reviewing');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate review');
      // Fall back to chatting if review generation fails
      setPhase('chatting');
    }
  }, [token, sessionId]);

  // ========================================
  // START OR RESUME INTERVIEW
  // ========================================

  const startOrResume = useCallback(
    async (email: string, name?: string, options?: { forceNew?: boolean }) => {
      if (!token) return;

      setPhase('starting');
      setError(null);
      setSummary(null);
      setReviewSummaries([]);
      setMessages([]);
      setTopicConfidence({});

      try {
        if (options?.forceNew) {
          const freshSession = await publicInterviewService.startInterview(
            token,
            name,
            email,
            'open_questions'
          );

          setSessionId(freshSession.session_id);
          setTotalTopics(freshSession.total_topics);
          setCurrentTopicIndex(freshSession.current_topic_index);
          if (freshSession.topics?.length) setTopics(freshSession.topics);
          if (freshSession.topic_confidence) setTopicConfidence(freshSession.topic_confidence);

          sessionStorage.setItem(`interview-session-${token}`, freshSession.session_id);
          sessionStorage.setItem(`interview-email-${token}`, email);

          addMessage({
            speaker: 'interviewer',
            content: freshSession.first_question,
            suggestedAnswers:
              freshSession.suggested_answers?.length > 0 ? freshSession.suggested_answers : undefined,
            suggestionSelectionType: freshSession.suggestion_selection_type || 'single',
          });
          setPhase('chatting');
          return;
        }

        const result = await publicInterviewService.startOrResume(token, email, name, 'default');

        setSessionId(result.session_id);
        setTotalTopics(result.total_topics);
        setCurrentTopicIndex(result.current_topic_index);
        if (result.topics?.length) setTopics(result.topics);
        if (result.topic_confidence) setTopicConfidence(result.topic_confidence);

        // Persist for page refresh resilience
        sessionStorage.setItem(`interview-session-${token}`, result.session_id);
        sessionStorage.setItem(`interview-email-${token}`, email);

        if (result.is_resumed && result.entries.length > 0) {
          // Resumed session: reconstruct message history
          const historicalMessages: ChatMessage[] = result.entries.map((entry) => ({
            id: entry.id,
            speaker: entry.speaker,
            content: entry.content,
            timestamp: new Date(entry.created_at),
            isStreaming: false,
          }));

          // Attach suggested answers to the last interviewer message
          if (result.suggested_answers?.length > 0) {
            const lastIdx = historicalMessages.reduce(
              (acc, msg, idx) => (msg.speaker === 'interviewer' ? idx : acc),
              -1
            );
            if (lastIdx >= 0) {
              historicalMessages[lastIdx].suggestedAnswers = result.suggested_answers;
              historicalMessages[lastIdx].suggestionSelectionType =
                result.suggestion_selection_type || 'single';
            }
          }

          setMessages(historicalMessages);

          // Check if resuming into review phase
          if (result.is_reviewing && result.review_summaries) {
            setReviewSummaries(result.review_summaries);
            setPhase('reviewing');
          } else {
            setPhase('chatting');
          }
        } else {
          // New session: show the first question
          addMessage({
            speaker: 'interviewer',
            content: result.first_question!,
            suggestedAnswers:
              result.suggested_answers?.length > 0 ? result.suggested_answers : undefined,
            suggestionSelectionType: result.suggestion_selection_type || 'single',
          });
          setPhase('chatting');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start interview');
        setPhase('email');
      }
    },
    [token, addMessage]
  );

  // ========================================
  // SEND MESSAGE + STREAM RESPONSE
  // ========================================

  const sendMessage = useCallback(
    async (content: string, skipTopic = false) => {
      if (!token || !sessionId || isStreaming) return;

      // Cancel any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Clear suggested answers from all previous messages
      clearSuggestedAnswers();

      // Add user message
      addMessage({
        speaker: 'user',
        content: skipTopic ? "[Skipped topic]" : content,
      });

      // Create placeholder for the AI response
      const responseMsgId = addMessage({
        speaker: 'interviewer',
        content: '',
        isStreaming: true,
      });

      setIsStreaming(true);
      setError(null);

      let responseContent = '';

      try {
        const stream = publicInterviewService.streamResponse(
          token,
          sessionId,
          content,
          skipTopic
        );

        for await (const event of stream) {
          if (abortControllerRef.current?.signal.aborted) break;
          handleStreamEvent(event, responseMsgId, responseContent, (c) => {
            responseContent = c;
          });
        }

        // Mark message as not streaming
        updateMessage(responseMsgId, { isStreaming: false });
      } catch (err) {
        updateMessage(responseMsgId, {
          content: 'Sorry, I encountered an error. Please try again.',
          isStreaming: false,
        });
        setError(err instanceof Error ? err.message : 'Failed to send message');
      } finally {
        setIsStreaming(false);
      }
    },
    [token, sessionId, isStreaming, addMessage, updateMessage, clearSuggestedAnswers]
  );

  // ========================================
  // HANDLE STREAM EVENTS
  // ========================================

  const handleStreamEvent = useCallback(
    (
      event: PublicStreamEvent,
      responseMsgId: string,
      currentContent: string,
      setContent: (c: string) => void
    ) => {
      const { event_type, data } = event;

      switch (event_type) {
        case 'content': {
          const chunk = data.chunk || data.content;
          if (chunk) {
            const newContent = currentContent + chunk;
            setContent(newContent);
            updateMessage(responseMsgId, { content: newContent });
          }
          break;
        }

        case 'suggested_answers': {
          const suggestions = data.suggestions as string[] | undefined;
          if (suggestions?.length) {
            updateMessage(responseMsgId, {
              suggestedAnswers: suggestions,
              suggestionSelectionType: (data.selection_type as 'single' | 'multi') || 'single',
            });
          }
          break;
        }

        case 'topic_change': {
          if (data.is_complete) {
            // Instead of completing directly, enter the review step
            if (data.completion_message) {
              updateMessage(responseMsgId, {
                content: data.completion_message,
                isStreaming: false,
              });
            }
            // Trigger review after a short delay to show the completion message (deduped)
            if (reviewTransitionTimeoutRef.current) clearTimeout(reviewTransitionTimeoutRef.current);
            reviewTransitionTimeoutRef.current = setTimeout(() => {
              reviewTransitionTimeoutRef.current = null;
              enterReview();
            }, 1500);
          } else {
            if (data.current_topic_index !== undefined) {
              setCurrentTopicIndex(data.current_topic_index);
            }
            if (data.next_question) {
              updateMessage(responseMsgId, {
                content: data.next_question,
                isStreaming: false,
              });
            }
            // Suggested answers for the new topic's question
            const suggestions = data.suggested_answers as string[] | undefined;
            if (suggestions?.length) {
              updateMessage(responseMsgId, {
                suggestedAnswers: suggestions,
                suggestionSelectionType: (data.suggestion_selection_type as 'single' | 'multi') || 'single',
              });
            }
          }
          break;
        }

        case 'complete': {
          // Instead of completing directly, enter the review step
          if (data.completion_message) {
            updateMessage(responseMsgId, {
              content: data.completion_message,
              isStreaming: false,
            });
          }
          if (reviewTransitionTimeoutRef.current) clearTimeout(reviewTransitionTimeoutRef.current);
          reviewTransitionTimeoutRef.current = setTimeout(() => {
            reviewTransitionTimeoutRef.current = null;
            enterReview();
          }, 1500);
          break;
        }

        case 'error': {
          setError(data.message || 'Unknown error');
          updateMessage(responseMsgId, {
            content: data.message || 'An error occurred.',
            isStreaming: false,
          });
          break;
        }

        case 'progress':
          if (data.current_topic_index !== undefined) {
            setCurrentTopicIndex(data.current_topic_index);
          }
          break;

        case 'confidence_update':
          if (data.current_topic_index !== undefined) {
            setCurrentTopicIndex(data.current_topic_index);
          }
          if (data.topic_confidence) {
            setTopicConfidence((prev) => ({ ...prev, ...data.topic_confidence }));
          }
          break;

        default:
          break;
      }
    },
    [updateMessage, enterReview]
  );

  // ========================================
  // SELECT SUGGESTED ANSWER
  // ========================================

  const selectSuggestedAnswer = useCallback(
    (answer: string) => {
      sendMessage(answer);
    },
    [sendMessage]
  );

  const submitMultiSelectAnswers = useCallback(
    (answers: string[]) => {
      if (answers.length === 0) return;
      const bulletList = answers.map((a) => `- ${a}`).join('\n');
      sendMessage(bulletList);
    },
    [sendMessage]
  );

  // ========================================
  // SKIP TOPIC
  // ========================================

  const skipTopic = useCallback(() => {
    sendMessage('[skip]', true);
  }, [sendMessage]);

  // ========================================
  // NAVIGATE TO TOPIC
  // ========================================

  const navigateToTopic = useCallback(
    async (topicIndex: number) => {
      if (!token || !sessionId || isStreaming || isNavigating) return;

      setIsNavigating(true);
      setError(null);
      clearSuggestedAnswers();

      try {
        const result = await publicInterviewService.navigateToTopic(token, sessionId, topicIndex);

        setCurrentTopicIndex(result.current_topic_index);

        // Add the new question as an interviewer message
        addMessage({
          speaker: 'interviewer',
          content: result.question,
          suggestedAnswers:
            result.suggested_answers?.length > 0 ? result.suggested_answers : undefined,
          suggestionSelectionType: result.suggestion_selection_type || 'single',
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to navigate to topic');
      } finally {
        setIsNavigating(false);
      }
    },
    [token, sessionId, isStreaming, isNavigating, addMessage, clearSuggestedAnswers]
  );

  // ========================================
  // REVIEW ACTIONS
  // ========================================

  const updateReviewTopic = useCallback(
    async (topicId: string, editedSummary: string) => {
      if (!token || !sessionId) return;

      // Capture previous value before optimistic update so rollback restores it, not null
      const previousEdit = reviewSummaries.find((s) => s.topic_id === topicId)?.edited_summary ?? null;

      // Optimistic update
      setReviewSummaries((prev) =>
        prev.map((s) =>
          s.topic_id === topicId ? { ...s, edited_summary: editedSummary } : s
        )
      );
      setIsUpdatingReview(true);

      try {
        const result = await publicInterviewService.updateReviewTopic(
          token, sessionId, topicId, editedSummary
        );
        setTopicConfidence(result.topic_confidence);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save edit');
        // Restore previous value (not null) so prior saved edits are preserved
        setReviewSummaries((prev) =>
          prev.map((s) =>
            s.topic_id === topicId ? { ...s, edited_summary: previousEdit } : s
          )
        );
      } finally {
        setIsUpdatingReview(false);
      }
    },
    [token, sessionId, reviewSummaries]
  );

  const confirmReview = useCallback(async () => {
    if (!token || !sessionId) return;

    setPhase('confirming');
    setError(null);

    try {
      const result = await publicInterviewService.confirmReview(token, sessionId);
      setSummary(result.summary);
      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm review');
      setPhase('reviewing');
    }
  }, [token, sessionId]);

  const backToChat = useCallback(async () => {
    if (!token || !sessionId) return;

    try {
      await publicInterviewService.backToChat(token, sessionId);
      setPhase('chatting');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to return to chat');
    }
  }, [token, sessionId]);

  // ========================================
  // COMPLETE INTERVIEW (legacy, kept for direct completion)
  // ========================================

  const completeInterview = useCallback(async () => {
    if (!token || !sessionId) return;

    setPhase('confirming');
    setError(null);

    try {
      const result = await publicInterviewService.completeInterview(token, sessionId);
      setSummary(result.summary);
      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete interview');
      setPhase('chatting');
    }
  }, [token, sessionId]);

  return {
    // Phase
    phase,

    // Derived convenience booleans
    isLoading: phase === 'loading',
    isStarting: phase === 'starting',
    isComplete: phase === 'complete',
    isReviewing: phase === 'reviewing',
    isGeneratingReview: phase === 'generating_review',
    isConfirming: phase === 'confirming',

    // Core state
    interviewerInfo,
    messages,
    sessionId,
    summary,
    error,
    totalTopics,
    currentTopicIndex,
    topics,
    topicConfidence,

    // Orthogonal state
    isStreaming,
    isNavigating,
    isUpdatingReview,

    // Review state
    reviewSummaries,

    // Actions
    startOrResume,
    sendMessage,
    selectSuggestedAnswer,
    submitMultiSelectAnswers,
    skipTopic,
    navigateToTopic,
    completeInterview,
    enterReview,
    updateReviewTopic,
    confirmReview,
    backToChat,
  };
}
